
import React, { useRef } from 'react';
import { Database, Download, Upload, ShieldAlert, FileJson, RefreshCw, Trash2, History } from 'lucide-react';
import { exportSystemBackup, importSystemBackup, performFactoryReset } from '../../services/backupService';
import { toast } from 'react-hot-toast';

export const SettingsTabData: React.FC = () => {
  const backupInputRef = useRef<HTMLInputElement>(null);

  const handleBackupImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await importSystemBackup(file);
  };

  const handleClearHistoryOnly = () => {
    if (window.confirm("Isso apagará TODOS os pedidos (Ativos e Finalizados). Suas configurações de frota e chaves API serão preservadas. Continuar?")) {
      localStorage.removeItem('sal_orders');
      toast.success('Histórico de pedidos limpo!');
      setTimeout(() => window.location.reload(), 1000);
    }
  };

  const SectionHeader = ({ icon: Icon, title, subTitle, color = 'text-sal-cyber-teal' }: any) => (
    <div className="flex items-center gap-3 border-b border-border-dim pb-4 mb-6">
      <div className={`p-2.5 bg-card-bg border border-border-dim rounded-xl ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h4 className="text-base font-black text-text-primary uppercase tracking-widest">{title}</h4>
        <p className="text-xs text-text-muted font-bold uppercase tracking-tighter">{subTitle}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300 pb-28">
      <div>
        <h3 className="text-3xl font-black text-text-primary mb-1 uppercase tracking-tighter">Soberania de Dados</h3>
        <p className="text-sm text-text-muted font-medium">Controle total sobre seus backups e persistência local.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Export / Import Cards */}
        <section className="bg-card-bg border border-border-dim rounded-[2.5rem] p-8 shadow-xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-[0.02] pointer-events-none group-hover:opacity-[0.04] transition-opacity">
            <FileJson className="w-48 h-48 text-text-primary" />
          </div>
          <SectionHeader icon={Database} title="Portabilidade Operacional" subTitle="Exportar e importar estado atual do sistema" />

          <div className="space-y-4 relative z-10">
            <div className="bg-bg-main/50 p-6 rounded-3xl border border-border-dim space-y-4 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-status-ready/10 text-status-ready rounded-2xl border border-status-ready/20">
                  <Download className="w-5 h-5" />
                </div>
                <div>
                  <h5 className="text-sm font-black text-text-primary uppercase tracking-widest">Extrair Backup</h5>
                  <p className="text-xs text-text-muted font-bold uppercase tracking-tighter mt-1 leading-tight">Salva todos os pedidos, frotas e regras em um arquivo .JSON</p>
                </div>
              </div>
              <button
                onClick={exportSystemBackup}
                className="w-full py-4 bg-status-ready text-badge-text-inv font-black rounded-2xl text-xs shadow-lg hover:brightness-110 transition-all uppercase tracking-[0.2em] active:scale-[0.98]"
              >
                Gerar Download
              </button>
            </div>

            <div className="bg-bg-main/50 p-6 rounded-3xl border border-border-dim space-y-4 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-status-info/10 text-status-info rounded-2xl border border-status-info/20">
                  <Upload className="w-5 h-5" />
                </div>
                <div>
                  <h5 className="text-sm font-black text-text-primary uppercase tracking-widest">Restaurar Estado</h5>
                  <p className="text-xs text-text-muted font-bold uppercase tracking-tighter mt-1 leading-tight">Substitui os dados atuais pelos dados do arquivo carregado</p>
                </div>
              </div>
              <input type="file" ref={backupInputRef} className="hidden" accept=".json" onChange={handleBackupImport} />
              <button
                onClick={() => backupInputRef.current?.click()}
                className="w-full py-4 bg-status-info text-badge-text-inv font-black rounded-2xl text-xs shadow-lg hover:brightness-110 transition-all uppercase tracking-[0.2em] active:scale-[0.98]"
              >
                Carregar Arquivo
              </button>
            </div>
          </div>
        </section>

        {/* Zona de Perigo */}
        <section className="bg-status-critical/5 border border-status-critical/20 rounded-[2.5rem] p-8 shadow-xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-[0.02] pointer-events-none group-hover:opacity-[0.04] transition-opacity">
            <ShieldAlert className="w-48 h-48 text-status-critical" />
          </div>
          <SectionHeader icon={ShieldAlert} title="Zona Crítica" subTitle="Ações irreversíveis de limpeza" color="text-rose-500" />

          <div className="space-y-4 relative z-10">
            {/* NOVO: Limpar Apenas Pedidos */}
            <div className="bg-bg-main/30 p-6 rounded-3xl border border-status-critical/10 space-y-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-status-pending/10 text-status-pending rounded-2xl border border-status-pending/20">
                  <History className="w-5 h-5" />
                </div>
                <div>
                  <h5 className="text-sm font-black text-status-pending uppercase tracking-widest">Limpar Apenas Pedidos</h5>
                  <p className="text-xs text-text-muted font-bold uppercase tracking-tighter mt-1 leading-tight">Apaga o histórico de entregas, mas mantém suas configurações e API Keys.</p>
                </div>
              </div>
              <button
                onClick={handleClearHistoryOnly}
                className="w-full py-4 bg-card-bg border border-status-pending/40 text-status-pending hover:bg-status-pending/5 font-black rounded-2xl text-xs transition-all uppercase tracking-[0.2em] flex items-center justify-center gap-3 active:scale-[0.98]"
              >
                <RefreshCw className="w-4 h-4" /> Resetar Turno
              </button>
            </div>

            <div className="bg-bg-main/30 p-6 rounded-3xl border border-status-critical/10 space-y-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-status-critical/10 text-status-critical rounded-2xl border border-status-critical/20">
                  <RefreshCw className="w-5 h-5" />
                </div>
                <div>
                  <h5 className="text-sm font-black text-status-critical uppercase tracking-widest">Redefinição de Fábrica</h5>
                  <p className="text-xs text-text-muted font-bold uppercase tracking-tighter mt-1 leading-tight">Apaga permanentemente todos os dados e retorna às configurações padrão.</p>
                </div>
              </div>
              <button
                onClick={() => { if (window.confirm("ISSO APAGARÁ TUDO (Configurações e Pedidos). Tem certeza?")) performFactoryReset(); }}
                className="w-full py-4 bg-card-bg border border-status-critical/40 text-status-critical hover:bg-status-critical/5 font-black rounded-2xl text-xs transition-all uppercase tracking-[0.2em] flex items-center justify-center gap-3 active:scale-[0.98]"
              >
                <Trash2 className="w-4 h-4" /> Executar Redefinição Total
              </button>
            </div>

            <div className="p-6 bg-status-critical/5 border border-status-critical/10 rounded-2xl italic text-xs text-text-muted font-medium leading-relaxed">
              * O sistema armazena todos os dados localmente no seu navegador. A limpeza do cache do navegador também resultará na perda desses dados se não houver um backup externo.
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
